import { Component, h, Prop, Element, Event, EventEmitter, Host, Listen } from '@stencil/core';

@Component({
	tag: 'kdx-dropdown-list',
	styleUrl: 'kdx-dropdown-list.scss',
	shadow: false
})
export class DropdownList {

	/**
     * Bind 'this' to updateActiveIndex
     */
	constructor() {
		this.updateActiveIndex = this.updateActiveIndex.bind(this);
	}

	@Element() root: HTMLElement;

	/**
	 * If true, the dropdown is a required component of the form
	 */
	@Prop() isRequired: boolean = false;
	/**
	 * The current value of the select tag
	 */
	@Prop({
		mutable: true,
		reflect: true
	}) activeItemIndex: number = 0;
	/**
	 * Additional css classes
	 */
	@Prop() classes?: string;
	/**
	 * If true, the dropdown items become anchor tags
	 */
	@Prop() containsLinks: boolean;
	/**
	 * Placeholder text for custom select
	 */
	@Prop() dropdownText: string;
	/**
	 * The ID of the select tag
	 */
	@Prop({
		mutable: true,
		reflect: true
	}) selectId: string;
	/**
	 * The name attribute of the select tag
	 */
	@Prop() selectName: string;
	/**
	 * The dropdown icon
	 */
	@Prop() selectIcon: string = "chevron-down";
	
	@Prop() labelText?: string;
	  
	/**
	 * Generated GUID for toggling custom dropdown
	 */
	guid: string = Math.random().toString(36).slice(2);
	/**
	 * Select options list
	 */
	selectOptions: Array<HTMLOptionElement> = [];

	// Used to share the selected value between other components
	@Event() selectUpdated: EventEmitter;

	// Get the active index from the custom select and update the native select value
	@Event() activeIndexUpdated: EventEmitter;	
	@Listen('activeIndexUpdated', {target: 'document'})
	activeIndexUpdatedHandler(event: CustomEvent<{elem: string, val: number}>) {
	  if(event.detail.elem == this.selectId && event.detail.val >= 0 && this.selectOptions.length >= event.detail.val + 1) {
		  const currVal = this.selectOptions[event.detail.val].value;
		  const el = (document.getElementById(this.selectId) as HTMLSelectElement);
		  el.value = currVal;
		  el.selectedIndex = event.detail.val;
		  el.dispatchEvent(new window.Event('change', { bubbles: true }));
	  }
	}

	/**
     * Get the custom select option chosen, update the display value, and trigger the index updated event
     */
	updateActiveIndex(event: Event) {
		this.activeItemIndex = parseInt((event.target as HTMLElement).getAttribute('item-index') || '0');
		(this.root.querySelector('.kdx-dropdown-list > .dropdown-select > .dropdown-text') as HTMLElement).innerText = ((event.target as HTMLElement)).innerText;
		this.closeDropdown();
		this.activeIndexUpdated.emit({elem: this.selectId, val: this.activeItemIndex});
	}

	/**
     * Toggles the custom select dropdown visibility
	 * 
	 * @param event - onClick event
     */
	handleClick(event: Event) {
		event.preventDefault();
		const targetElem = event.target as HTMLElement;

		(targetElem.closest('.kdx-dropdown-list') as HTMLElement).classList.toggle('show');
		const dropdownBoxHeight = (targetElem.closest('.kdx-dropdown-list') as HTMLElement).clientHeight
		const menuElem = (targetElem.closest('.kdx-dropdown-list') as HTMLElement).querySelector('.dropdownmenu');
		menuElem!.classList.toggle('show');
		const rect = menuElem!.getBoundingClientRect();
		//set margin to negative value to move menu up
		if ((window.innerHeight - rect.top) < rect.height && rect.height < rect.top)
			(menuElem as any).style.marginTop = "-" + (rect.height + dropdownBoxHeight).toString() + "px";

		//when closing dropdownmenu, remove top margin
		if (!menuElem?.classList.contains('show'))
			(menuElem as any).style.marginTop = "0";
	}

	/**
	 * Closes the custom select dropdown menu
	 *
	 * @remarks
	 * Used to manually close list
	 */
	closeDropdown() {
		(this.root.querySelector('.kdx-dropdown-list') as HTMLElement).classList.remove('show');
		(this.root.querySelector('.kdx-dropdown-list > .dropdownmenu') as HTMLElement).classList.remove('show');
	}

	/**
     * Builds a URL based on value of the selected option and then does a redirect
	 * simulating a "click browser link" experience.
	 * 
	 * @remarks
	 * Used alongside the 'containsLinks' prop
	 * 
	 * @param optionIndex - The currently selected value of the native select
     */
	redirectOptionLink(optionIndex: HTMLOptionElement) {
		if(optionIndex && optionIndex.value.startsWith("http")) {
			window.location.href = optionIndex.value;
		}
		else if (optionIndex && optionIndex.value.startsWith("/")) {
			window.location.href = window.location.origin + optionIndex.value;
		}
		else {
			window.location.href = this.getQueriesLink(optionIndex.value);
		}
	}

	/**
	 * Generates URL by comparing new query values with old ones, replacing/adding as neccessary
	 * 
	 * @remarks
	 * Used alongside 'containsLinks' prop. Called by redirectOptionLink() and render() when rendering anchor tags
	 * 
	 * @param newParams - The query string
	 * 
	 * @returns
	 * The newly generated URL
	 */
	getQueriesLink(newParams) {
		const newSearchParams = new URLSearchParams(newParams);
		const urlSearchParams = new URLSearchParams(window.location.search);

		for (const[k, v] of newSearchParams) {
			if(urlSearchParams.has(k))
				urlSearchParams.set(k, v);
			else
				urlSearchParams.append(k, v);
		}
		
		return window.location.href.split('?')[0] + '?' + urlSearchParams.toString();
	}

	componentWillLoad() {
		// Create unique ID
		this.selectId = (!this.selectId) ? "kdx-dropdown-list-" + this.guid : this.selectId;
		// Add click listener for expanding the custom dropdown
		document.addEventListener('click', (event) => {
			if (this.root.querySelector('.kdx-dropdown-list.show') && ! (this.root.querySelector('.kdx-dropdown-list.show')?.contains(event.target as HTMLElement)) ) {
				this.closeDropdown();
			};
		});
		
		// Get the options list
		this.selectOptions = [...this.root.querySelectorAll<HTMLOptionElement>(':scope > option')];
		// Get the last selected option, falling back to the first one
		const selectedOption: HTMLOptionElement | undefined = [...this.selectOptions].reverse().find(option => option.selected) ?? this.selectOptions[0];
		this.activeItemIndex = this.selectOptions.indexOf(selectedOption);
	}

	componentDidLoad() {
	
		this.root.querySelector('select').selectedIndex = this.activeItemIndex;
		// Get placeholder text if no prop was provided
		if(!this.dropdownText) {
			(this.root.querySelector('.kdx-dropdown-list > .dropdown-select > .dropdown-text') as HTMLElement).innerHTML = 
				(this.selectOptions.length >= this.activeItemIndex + 1) ? this.selectOptions[this.activeItemIndex].label : this.selectOptions[0].label;
		}
		// If both dropdownText and activeItemIndex are provided, activeItemIndex takes priority 
		else if (this.dropdownText && this.activeItemIndex > 0) {
			(this.root.querySelector('.kdx-dropdown-list > .dropdown-select > .dropdown-text') as HTMLElement).innerText = 
        (this.selectOptions.length >= this.activeItemIndex + 1) ? this.selectOptions[this.activeItemIndex].label : this.dropdownText;
		}
		
	}

	componentDidRender() {
		// Add event listener for clicking custom dropdown options
		if (!this.containsLinks) {
			Array.from(this.root.querySelectorAll('.kdx-dropdown-list > .dropdownmenu > .dropdown-item')).forEach((link, index) => {
				link.setAttribute('item-index', `${index}`);
				link.addEventListener('click', this.updateActiveIndex);
			});
		}
	}

	render() {
		return (
			<Host>
				{this.labelText ?
					<label class='hidden' htmlFor={this.selectId}>{this.labelText}</label> 
				: ""}
				<select 
					class={`dropdown-select ${this.classes ? ` ${this.classes}` : ''}`}
					id={this.selectId}
					name={this.selectName}
					autocomplete="off"
					required={this.isRequired}
					onChange={() => {
						let el = document.getElementById(this.selectId) as HTMLSelectElement;
						(this.root.querySelector('.kdx-dropdown-list > .dropdown-select > .dropdown-text') as HTMLElement).innerText = 
							(el[el.selectedIndex]).label;

						if(this.containsLinks) {
							this.redirectOptionLink(el[el.selectedIndex] as HTMLOptionElement);
						}
						
						this.selectUpdated.emit({elem: this.selectId, val: el.selectedIndex});
					}}
				>
					<slot />
				</select>
				<span class="svg-icon">
					<kdx-icon icon={this.selectIcon}></kdx-icon>
				</span>
				<div 
					class={`kdx-dropdown-list ${this.classes ? ` ${this.classes}` : ''}`} 
					id={`custom-${this.selectId}`}
					aria-hidden="true"
					tabindex="-1"
				>
					<a 
						class="dropdown-select"
					   	id={`toggle-${this.guid}`}
					   	href="#"
					   	aria-hidden="true"
					   	onClick={this.handleClick}
					   	tabindex="-1"
					>
						<span class="dropdown-text">
							{this.dropdownText}
						</span>
						<button class="svg-icon" tabindex="-1">
							<kdx-icon icon={this.selectIcon}></kdx-icon>
						</button>
					</a>

					<div 
						class="dropdownmenu" 
						aria-labelledby={`toggle-${this.guid}`} 
						active-item={this.activeItemIndex}
					>
						{this.containsLinks ? 
							this.selectOptions.map((option) => {
								if (!option.getAttribute("value"))
									option.value = "";
								return <a 
										  class={{'dropdown-item': true, 'hidden': option.disabled || option.hidden}}
										  href={(option.value.startsWith("http") || option.value.startsWith("/")) ? option.value : this.getQueriesLink(option.value)} 
										  title={option.label}
									   >
										  {option.label}
									   </a>
							}) 
							:
							this.selectOptions.map((option) => {
								if (!option.getAttribute("value"))
									option.value = "";
								return <div 
											class={{'dropdown-item': true, 'hidden': option.disabled || option.hidden}} 
											data-value={option.value}
									   >
											{option.label}
									   </div>
							})
						}
					</div>
				</div>
			</Host>
		);
	}
}
